<script setup lang="ts">
import { Button } from '@/components/ui/button';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Layers3 } from 'lucide-vue-next';

interface GroupOption {
  value: string;
  label: string;
}

const props = withDefaults(defineProps<{
  modelValue: string;
  options: ReadonlyArray<GroupOption>;
  taskCount: number | string;
  storageKey?: string;
  itemLabel?: string;
  itemPluralLabel?: string;
}>(), {
  storageKey: 'tasks-group-by',
  itemLabel: 'task',
});

const emit = defineEmits<{
  (event: 'update:model-value', value: string): void;
}>();
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <SearchableSelectMenu
      :model-value="modelValue"
      :options="options"
      placeholder="Group by"
      search-placeholder="Search grouping..."
      :storage-key="storageKey"
      empty-state-label="Search grouping options."
      trigger-class="inline-flex h-8 items-center gap-2 rounded-full border border-border/70 bg-card px-2.5 text-left text-sm shadow-sm transition-colors hover:bg-muted/45"
      @update:model-value="value => emit('update:model-value', String(value || 'none'))"
    >
      <template #trigger="{ option }">
        <Layers3 class="size-3.5 text-muted-foreground" />
        <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
          {{ option?.label || 'No grouping' }}
        </span>
      </template>
    </SearchableSelectMenu>

    <Button
      v-if="modelValue !== 'none'"
      type="button"
      variant="ghost"
      size="sm"
      class="h-8 rounded-full px-3 text-xs text-muted-foreground"
      @click="emit('update:model-value', 'none')"
    >
      Clear grouping
    </Button>

    <span class="text-sm text-muted-foreground">
      {{ taskCount }} {{ Number(taskCount) === 1 ? itemLabel : (itemPluralLabel || (itemLabel === 'entry' ? 'entries' : `${itemLabel}s`)) }}
    </span>
  </div>
</template>
